import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError, of } from 'rxjs';
import { catchError, filter, shareReplay, take } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { UserPermissions, ChannelPermission, DefaultLandingPageMap } from '../models/permission.interface';
import { environment } from '../../environments/environment';
import { GET_USER_PERMISSIONS, LOG_OUT_URL } from '../config';
import { ApiService } from '../pages/services/api.service';

const CHANNELS = ['email', 'sms', 'rcs', 'whatsapp'] as const;
type Channel = typeof CHANNELS[number];

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private userPermissionsSubject = new BehaviorSubject<UserPermissions | null>(null);
  public userPermissions$ = this.userPermissionsSubject.asObservable();

  private defaultLandingPageMap: DefaultLandingPageMap | null = null;

  private permissionsLoaded = false;
  private loadingInProgress = false;
  /** After first full-page logout request, block duplicate redirects and extra permission HTTP churn. */
  private fullPageLogoutIssued = false;
  /** Multicast one in-flight GET so guards and AppComponent do not subscribe to userPermissions$ (initial null wiped permissions). */
  private inFlightPermissionsRequest$: Observable<any> | null = null;

  private canonicalizeResource(resource: string): string {
    return String(resource ?? '').trim().toLowerCase();
  }

  private normalizeAction(action: string): string {
    return String(action ?? '').trim().toLowerCase();
  }

  /** Canonicalize and store API response as-is (by channel). */
  private canonicalizeUserPermissions(data: Record<string, unknown>): UserPermissions {
    const result: UserPermissions = {};
    CHANNELS.forEach(channel => {
      const arr = data[channel];
      if (!Array.isArray(arr)) return;
      result[channel] = arr.map((item: any) => ({
        resource: this.canonicalizeResource(item.resource ?? ''),
        actions: Array.isArray(item.actions)
          ? Array.from(new Set((item.actions as string[]).map(a => this.normalizeAction(a))))
          : []
      }));
    });
    return result;
  }

  constructor(
    private http: HttpClient,
    private apiService: ApiService
  ) {}

  loadPermissions(): Observable<any> {
    if (this.fullPageLogoutIssued) {
      return of({ status: 0, data: {} });
    }
    if (this.permissionsLoaded) {
      return of(this.userPermissionsSubject.value!);
    }
    if (this.inFlightPermissionsRequest$ != null) {
      return this.inFlightPermissionsRequest$;
    }

    this.loadingInProgress = true;
    const apiUrl = `${environment.api_url}${GET_USER_PERMISSIONS}`;

    this.inFlightPermissionsRequest$ = this.http.get<any>(apiUrl).pipe(
      catchError(error => {
        console.error('Error fetching permissions:', error);
        this.inFlightPermissionsRequest$ = null;
        this.loadingInProgress = false;
        return throwError(error);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.inFlightPermissionsRequest$;
  }

  setUserPermissions(permissions: UserPermissions): void {
    this.userPermissionsSubject.next(permissions);
    this.permissionsLoaded = true;
  }

  arePermissionsLoaded(): boolean {
    return this.permissionsLoaded;
  }

  /** Idempotent: guards + API may both detect revoked session in the same tick. */
  redirectToLogoutOnce(): void {
    if (this.fullPageLogoutIssued) {
      return;
    }
    this.fullPageLogoutIssued = true;
    this.apiService.redirectToLogout(LOG_OUT_URL);
  }

  isFullPageLogoutIssued(): boolean {
    return this.fullPageLogoutIssued;
  }

  /**
   * Revoked session: unblock PermissionGuard's getPermissionsOnce (it filters out null and would hang until timeout).
   * Then full-page logout like ApiService does for status 0.
   */
  private invalidateSessionAndLogout(): void {
    if (this.fullPageLogoutIssued) {
      return;
    }
    this.loadingInProgress = false;
    this.inFlightPermissionsRequest$ = null;
    this.defaultLandingPageMap = null;
    this.userPermissionsSubject.next({});
    this.permissionsLoaded = true;
    this.redirectToLogoutOnce();
  }

  /**
   * Process and store permissions from API response.
   * New format: { data: { permissions: {...}, default_landing_page: {...} } }
   * Old format: { data: { email: [...], sms: [...], rcs: [...], whatsapp: [...] } }
   */
  processPermissionsResponse(response: any): void {
    // Match ApiService session handling: body status 0 → server logout (not an HTTP error).
    if (response != null && +response['status'] === 0) {
      this.invalidateSessionAndLogout();
      return;
    }

    const data = response?.data ?? {};
    const permissionsData = data.permissions != null ? data.permissions : data;
    const permissions = this.canonicalizeUserPermissions(permissionsData);

    // Successful permissions fetch means session is usable again — clear stuck logout flag (e.g. blocked window.location).
    this.fullPageLogoutIssued = false;

    // Set landing map before emitting permissions: userPermissions$ subscribers (e.g. PermissionGuard) run synchronously.
    if (data.default_landing_page != null && typeof data.default_landing_page === 'object') {
      this.defaultLandingPageMap = { ...data.default_landing_page };
    } else {
      this.defaultLandingPageMap = null;
    }
    this.userPermissionsSubject.next(permissions);
    this.permissionsLoaded = true;
    this.loadingInProgress = false;
    this.inFlightPermissionsRequest$ = null;
    console.log('User permissions loaded and cached:', permissions);
  }

  /**
   * Ensures guards waiting on userPermissions$ complete when the permissions HTTP call fails.
   * Mirrors reloadPermissions error handling so loadingInProgress is cleared and hasPermission checks run.
   */
  private failPermissionsLoad(message: string, error?: unknown): void {
    console.error(message, error ?? '');
    // Revoked session often surfaces as HTTP 401/403 or JSON body status 0 on error — same as ApiService session handling.
    const httpErr = error as HttpErrorResponse | undefined;
    const body = httpErr?.error as Record<string, unknown> | undefined;
    const sessionRevoked =
      httpErr?.status === 401 ||
      httpErr?.status === 403 ||
      (body != null && +body['status'] === 0);
    if (sessionRevoked) {
      this.invalidateSessionAndLogout();
      return;
    }
    this.loadingInProgress = false;
    this.inFlightPermissionsRequest$ = null;
    this.userPermissionsSubject.next({});
    this.permissionsLoaded = true;
  }

  reloadPermissions(): void {
    this.fullPageLogoutIssued = false;
    this.permissionsLoaded = false;
    this.loadingInProgress = false;
    this.inFlightPermissionsRequest$ = null;
    this.loadPermissions().subscribe({
      next: (response: any) => {
        this.processPermissionsResponse(response);
      },
      error: (error) => this.failPermissionsLoad('Error reloading user permissions:', error)
    });
  }

  /**
   * Check if user has required permissions (channel-specific).
   * @param requiredPermissions - Array of { channel, resource, action }
   */
  hasPermissions(requiredPermissions: Array<{ channel: string; resource: string; action: string | string[] }>): boolean {
    const data = this.userPermissionsSubject.value;
    if (!data) return false;

    return requiredPermissions.every(req => {
      const channel = (req.channel ?? '').toLowerCase();
      const list = channel && (data as Record<string, ChannelPermission[] | undefined>)[channel];
      if (!Array.isArray(list)) return false;

      const resource = this.canonicalizeResource(req.resource);
      const actions = Array.isArray(req.action)
        ? req.action.map(a => this.normalizeAction(a))
        : [this.normalizeAction(req.action)];

      const entry = list.find(p => this.canonicalizeResource(p.resource) === resource);
      if (!entry || !entry.actions) return false;
      return actions.some(a => entry.actions.includes(a));
    });
  }

  /**
   * Check if user has any of the required permissions (OR logic).
   * @param requiredPermissions - Array of { channel, resource, action }
   */
  hasAnyPermissions(requiredPermissions: Array<{ channel: string; resource: string; action: string | string[] }>): boolean {
    if (!requiredPermissions || requiredPermissions.length === 0) return false;
    const data = this.userPermissionsSubject.value;
    if (!data) return false;

    return requiredPermissions.some(req => {
      const channel = (req.channel ?? '').toLowerCase();
      const list = channel && (data as Record<string, ChannelPermission[] | undefined>)[channel];
      if (!Array.isArray(list)) return false;

      const resource = this.canonicalizeResource(req.resource);
      const actions = Array.isArray(req.action)
        ? req.action.map(a => this.normalizeAction(a))
        : [this.normalizeAction(req.action)];

      const entry = list.find(p => this.canonicalizeResource(p.resource) === resource);
      if (!entry || !entry.actions) return false;
      return actions.some(a => entry.actions.includes(a));
    });
  }

  /**
   * Check if user has a specific permission for a channel.
   * @param channel - Channel (email, sms, rcs, whatsapp)
   * @param resource - Resource name
   * @param action - Action name
   */
  hasPermission(channel: string, resource: string, action: string): boolean {
    return this.hasPermissions([{ channel, resource, action }]);
  }

  clearPermissions(): void {
    this.fullPageLogoutIssued = false;
    this.userPermissionsSubject.next(null);
    this.defaultLandingPageMap = null;
    this.permissionsLoaded = false;
    this.loadingInProgress = false;
    this.inFlightPermissionsRequest$ = null;
  }

  /**
   * Get the default landing page route for a product/channel code (e.g. emailapi, soip, sms, conv_apps).
   * Returns undefined when not loaded or key missing.
   */
  getDefaultLandingPage(productCode: string): string | undefined {
    console.log('getDefaultLandingPage', this.defaultLandingPageMap, productCode);
    if (!this.defaultLandingPageMap || !productCode) return undefined;
    const route = this.defaultLandingPageMap[productCode];
    return typeof route === 'string' && route.trim() !== '' ? route.trim() : undefined;
  }

  getCurrentPermissions(): UserPermissions | null {
    return this.userPermissionsSubject.value;
  }

  getPermissionsOnce(): Observable<UserPermissions> {
    if (!this.permissionsLoaded && !this.loadingInProgress) {
      this.loadPermissions().subscribe({
        next: (response) => this.processPermissionsResponse(response),
        error: (error) => this.failPermissionsLoad('Error loading user permissions:', error)
      });
    }
    return this.userPermissions$.pipe(
      filter((p): p is UserPermissions => p !== null),
      take(1)
    );
  }
}
